import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { useAuth } from "@/context/AuthContext";
import {
  Loader2, Save, FormInput, Lock, TrendingUp, BarChart3, Sparkles, ClipboardList,
} from "lucide-react";

const NAVY = "#1C4358";
const ScopeBadge = ({ kind }) => {
  const map = { GLOBAL: ["rgba(28,67,88,0.10)", NAVY], LOCAL: ["rgba(47,125,90,0.12)", "#2F7D5A"], HYBRID: ["rgba(183,121,31,0.14)", "#B7791F"] };
  const [bg, c] = map[kind];
  return <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ backgroundColor: bg, color: c }}>{kind}</span>;
};
const ic = "text-[15px] rounded-lg border-[#1C4358]/20 focus-visible:ring-0";

export default function IntakeAdmin() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [cfg, setCfg] = useState(null);
  const [schema, setSchema] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, a] = await Promise.all([api.get("/site/intake"), api.get("/site/intake/analytics", { params: { days: 30 } })]);
      setCfg(c.data.config);
      setSchema(c.data);
      setAnalytics(a.data);
    } catch { toast.error("Could not load form settings."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/site/intake", {
        welcome: cfg.welcome, thank_you_template: cfg.thank_you_template,
        ai_thank_you_enabled: cfg.ai_thank_you_enabled, ai_helper_enabled: cfg.ai_helper_enabled,
        local_intro: cfg.local_intro, step_titles: cfg.step_titles,
      });
      toast.success("Form settings saved.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div>;

  const t = analytics?.totals || {};

  return (
    <div className="max-w-4xl mx-auto pb-16" data-testid="intake-admin">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <FormInput size={22} style={{ color: NAVY }} />
            <h1 className="text-2xl font-display font-bold text-[#1C4358]">Forms & Conditional Intake</h1>
          </div>
          <p className="text-[14px] text-[#4B5563] mt-1">One smart intake engine across the site — with conditional questions, AI helper, and AI thank-you pages.</p>
        </div>
        {isSuper && <Button data-testid="intake-save-btn" onClick={save} disabled={saving} className="gap-1.5 bg-[#9C1E22] hover:bg-[#861A1D] text-white">{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save</Button>}
      </div>

      {!isSuper && (
        <div className="mt-4 flex items-center gap-2 rounded-lg bg-[rgba(28,67,88,0.05)] px-4 py-3 text-[14px] text-[#1C4358]"><Lock size={15} /> Global form logic is read-only for your role. You can view submissions and analytics in your scope.</div>
      )}

      {/* Analytics */}
      <div className="mt-6">
        <div className="flex items-center gap-2 mb-3"><BarChart3 size={18} style={{ color: NAVY }} /><h2 className="text-lg font-display font-bold text-[#1C4358]">Funnel (last 30 days)</h2></div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="intake-analytics">
          {[["Views", t.views], ["Starts", t.starts], ["Completions", t.completions], ["Abandon", t.abandon], ["Completion %", (t.completion_rate ?? 0) + "%"]].map(([lbl, val], i) => (
            <Card key={i} className="p-4 border-[#E6EDF2]">
              <div className="text-[12px] text-[#4B5563]">{lbl}</div>
              <div className="text-2xl font-display font-bold text-[#1C4358] mt-0.5">{val ?? 0}</div>
            </Card>
          ))}
        </div>
        {analytics?.by_situation && Object.keys(analytics.by_situation).length > 0 && (
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <Card className="p-4 border-[#E6EDF2]">
              <div className="text-[13px] font-semibold text-[#1C4358] mb-2 flex items-center gap-1"><TrendingUp size={14} /> Completions by situation</div>
              {Object.entries(analytics.by_situation).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                <div key={k} className="flex justify-between text-[13px] py-0.5"><span className="text-[#4B5563]">{k}</span><span className="font-semibold text-[#1C4358]">{v}</span></div>
              ))}
            </Card>
            <Card className="p-4 border-[#E6EDF2]">
              <div className="text-[13px] font-semibold text-[#1C4358] mb-2 flex items-center gap-1"><TrendingUp size={14} /> Completions by page</div>
              {Object.entries(analytics.by_page).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([k, v]) => (
                <div key={k} className="flex justify-between text-[13px] py-0.5"><span className="text-[#4B5563] truncate mr-2">{k}</span><span className="font-semibold text-[#1C4358]">{v}</span></div>
              ))}
            </Card>
          </div>
        )}
      </div>

      {/* Config */}
      <Accordion type="multiple" defaultValue={["copy", "ai", "questions"]} className="mt-8 space-y-3">
        <AccordionItem value="copy" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="font-display font-semibold text-[#1C4358]"><span className="flex items-center gap-2">Form copy <ScopeBadge kind="GLOBAL" /></span></AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <div className="space-y-1.5"><Label className="text-[13px] font-semibold text-[#1C4358]">Welcome line</Label><Textarea rows={2} className={ic} value={cfg.welcome || ""} disabled={!isSuper} onChange={(e) => set("welcome", e.target.value)} data-testid="intake-welcome" /></div>
            <div className="space-y-1.5">
              <Label className="text-[13px] font-semibold text-[#1C4358]">Thank-you template (fallback when AI is off)</Label>
              <Textarea rows={3} className={ic} value={cfg.thank_you_template || ""} disabled={!isSuper} onChange={(e) => set("thank_you_template", e.target.value)} data-testid="intake-thankyou-template" />
              <p className="text-[12px] text-[#4B5563]">Use <span className="font-mono">{"{name}"}</span>, <span className="font-mono">{"{situation}"}</span>, <span className="font-mono">{"{location}"}</span>.</p>
            </div>
            <div className="space-y-1.5"><Label className="text-[13px] font-semibold text-[#1C4358] flex items-center gap-2">Local intro line <ScopeBadge kind="LOCAL" /></Label><Input className={ic} value={cfg.local_intro || ""} disabled={!isSuper} onChange={(e) => set("local_intro", e.target.value)} placeholder="Shown at the top of step 1 (editable per franchise)" /></div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="ai" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="font-display font-semibold text-[#1C4358]"><span className="flex items-center gap-2"><Sparkles size={15} /> AI features <ScopeBadge kind="GLOBAL" /></span></AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <label className="flex items-center justify-between gap-3 text-[14px]"><span>AI-personalized thank-you pages</span><Switch checked={!!cfg.ai_thank_you_enabled} disabled={!isSuper} onCheckedChange={(v) => set("ai_thank_you_enabled", v)} data-testid="intake-ai-ty" /></label>
            <label className="flex items-center justify-between gap-3 text-[14px]"><span>AI helper text inside forms</span><Switch checked={!!cfg.ai_helper_enabled} disabled={!isSuper} onCheckedChange={(v) => set("ai_helper_enabled", v)} data-testid="intake-ai-helper" /></label>
            <p className="text-[12px] text-[#4B5563]">Compliance-safe: the AI never promises placement, funding, or sale outcomes and gives no legal/medical/financial advice.</p>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="questions" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
          <AccordionTrigger className="font-display font-semibold text-[#1C4358]"><span className="flex items-center gap-2"><ClipboardList size={15} /> Conditional questions <ScopeBadge kind="GLOBAL" /></span></AccordionTrigger>
          <AccordionContent className="space-y-4 pb-5">
            <p className="text-[13px] text-[#4B5563]">Master question logic is standardized (read-only) to keep every submission consistent and compliance-safe.</p>
            {schema && (schema.situations || []).map((s) => (
              <div key={s.key} className="rounded-lg border border-[#E6EDF2] p-3">
                <div className="font-semibold text-[#1C4358] text-[14px]">{s.label}</div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {((schema.conditional_questions || {})[s.key] || []).map((q) => (
                    <Badge key={q.key} variant="outline" className="text-[11px] border-[#1C4358]/20 text-[#4B5563]">{q.label}</Badge>
                  ))}
                </div>
              </div>
            ))}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
